import PortalIdParser from 'PortalIdParser';
import { ApiMap } from '../ApiMap';
import { createApiCache, MAX_CACHE_TIMEOUT } from './createApiCache';
let clearPropertyCache = false;
const CategoryCache = {};
export function getFetchCategory(http) {
  const withCategoryCache = createApiCache(http.get, ApiMap.PROPERTIES.getUrl, CategoryCache, MAX_CACHE_TIMEOUT);
  return function fetchCategory(category) {
    return withCategoryCache(category, {}, {
      clearCache: clearPropertyCache
    });
  };
}
const OwnersCache = {};
export function getFetchOwners(http) {
  const withOwnersCache = createApiCache(http.get, ApiMap.OWNERS.getUrl, OwnersCache, MAX_CACHE_TIMEOUT);
  return function fetchOwners() {
    return withOwnersCache('', {}, {
      clearCache: clearPropertyCache
    });
  };
}
const SiteSettingsCache = {};
export function getFetchSiteSettings(http) {
  const withSiteSettingsCache = createApiCache(http.get, ApiMap.SITE_SETTINGS.getUrl, SiteSettingsCache, MAX_CACHE_TIMEOUT);
  const portalId = PortalIdParser.get();
  return function fetchSiteSettings() {
    return withSiteSettingsCache(portalId, {}, {
      clearCache: clearPropertyCache
    });
  };
}
const CustomObjectMetaCache = {};
export function getFetchCustomObjectMeta(http) {
  const withCustomObjectMetaCache = createApiCache(http.get, ApiMap.CUSTOM_OBJECT_META.getUrl, CustomObjectMetaCache, MAX_CACHE_TIMEOUT);
  return function fetchCustomObjectMeta() {
    return withCustomObjectMetaCache('', {}, {
      clearCache: clearPropertyCache
    });
  };
}
const asCacheKey = (personalizeConfig = {}) => {
  const supportedCrmObjectFQNs = personalizeConfig.supportedCrmObjectFQNs || [];
  return supportedCrmObjectFQNs.join(',');
};
const CustomObjectLabelsMapCache = {};
const fetchCustomObjectLabelsMap = (fetchCustomObjectMeta, personalizeConfig) => {
  const cacheKey = asCacheKey(personalizeConfig);
  if (CustomObjectLabelsMapCache[cacheKey] && !clearPropertyCache) {
    return new Promise(resolve => resolve(CustomObjectLabelsMapCache[cacheKey]));
  } else {
    return new Promise(resolve => {
      fetchCustomObjectMeta().then(({
        results
      }) => {
        CustomObjectLabelsMapCache[cacheKey] = results.reduce((customObjectLabels, {
          labels,
          fullyQualifiedName
        }) => {
          customObjectLabels[fullyQualifiedName] = labels.singular;
          return customObjectLabels;
        }, {});
        resolve(CustomObjectLabelsMapCache[cacheKey]);
      }).catch(() => {
        const {
          supportedCrmObjectFQNs = []
        } = personalizeConfig;
        // we need a sensible default here because this request blocks
        // the requests to actually fetch the properties for each category
        // and even if we can't get the labels for the custom object categories,
        // we need to ensure we get the properties for them (and the HS-defined
        // categories)
        CustomObjectLabelsMapCache[cacheKey] = supportedCrmObjectFQNs.reduce((customObjectLabels, fqn) => {
          customObjectLabels[fqn] = fqn;
          return customObjectLabels;
        }, {});
        resolve(CustomObjectLabelsMapCache[cacheKey]);
      });
    });
  }
};
const PropertiesPromiseCache = {};
export function getFetchProperties(http, invalidateCache = false) {
  const fetchCustomObjectMeta = getFetchCustomObjectMeta(http);
  const fetchCategory = getFetchCategory(http);
  clearPropertyCache = invalidateCache;
  return function fetchProperties(personalizeConfig) {
    const {
      supportedCrmObjectFQNs = []
    } = personalizeConfig;
    const cacheKey = asCacheKey(personalizeConfig);
    if (Object.prototype.hasOwnProperty.call(PropertiesPromiseCache, cacheKey) && !invalidateCache) {
      return PropertiesPromiseCache[cacheKey];
    }
    return fetchCustomObjectLabelsMap(fetchCustomObjectMeta, personalizeConfig).then(
    // first fetch human-readable labels for custom objects since we don't
    // have translations for these: we just want to give back the original
    // label that was specified when the custom object was created
    customObjectLabels => {
      PropertiesPromiseCache[cacheKey] = Promise.all(supportedCrmObjectFQNs.map(fqn => {
        return fetchCategory(encodeURIComponent(fqn)).then(propertiesForCategory => {
          return {
            fqn,
            propertiesForCategory,
            customObjectLabels
          };
        });
      }));
      return PropertiesPromiseCache[cacheKey];
    });
  };
}